import React, { useState, useCallback, Suspense } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import colors from 'styles/variables/baseColors.module.scss';
import { Box, makeStyles, Typography } from '@material-ui/core';
import JoinButton from 'components/Guest/PreJoin/JoinButton';
import JoinFormGreeting from 'components/Guest/PreJoin/JoinFormGreeting';
import TermsOfService from 'components/Guest/PreJoin/TOS';
import EmailInput from 'components/Guest/PreJoin/EmailInput';
import NameInput from 'components/Guest/PreJoin/NameInput';
import { LsKey, lsSet } from 'utils/localStorageUtils';
import { GUEST_JOIN } from 'constants/ScheduleModal';
import actions from 'actions/Contact';
import { guestLogin as guestLoginAction } from 'actions/Auth';
import LoadingComp from 'components/LoadingAnimation/LoadingAnimation';
import AudioVideoSettings from 'components/Guest/AudioVideoSettings/AudioVideoSettings';
import { setCallAttempted } from 'reducers/guestFlowSlice';
import { RootState } from 'store/reducers';
import { Analytics } from '@lifesize/web-clients-core';
import { nanoid } from 'nanoid';
import { TrackEvents } from 'utils/analytics';
import Logger from 'js-logger';

export const UserInformationForm = () => {
  const classes = userFieldStyles();
  const dispatch = useDispatch();
  const [hasClickedJoinMeeting] = useState(false);
  const [avDrawerOpen, toggleAvDrawer] = useState(false);
  const guestFlow = useSelector((state: RootState) => state?.app?.guestFlow);
  const guestForm = useSelector((state: any) => state.app?.form[GUEST_JOIN]?.values);
  const { name, email } = guestForm;
  const guestLogin = useCallback((name, extension, email): any => dispatch(guestLoginAction(name, extension, email)), [
    dispatch
  ]);

  const handleJoinMeeting = useCallback(async () => {
    if (!guestFlow?.compliance?.acceptedTos || !guestForm?.name) return;
    name && lsSet(LsKey.Name, name);
    email && lsSet(LsKey.Email, email);
    const user = {
      id: `guest-${nanoid()}`,
      role: 'guest',
      email,
      name
    };
    Analytics.identify(user);
    await guestLogin(name, guestFlow?.extension, email);
    await dispatch(actions.startVideoCall(guestFlow?.extension, ''));
    dispatch(setCallAttempted(true));
    Analytics.track(TrackEvents.Call, { extension: guestFlow.extension });
    Logger.info('Call started');
  }, [dispatch, email, guestFlow, guestForm, guestLogin, name]);

  const onKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key.toLowerCase() === 'enter') {
      await handleJoinMeeting();
    }
  };

  const handleSettingsClicked = () => {
    toggleAvDrawer(true);
  };

  return (
    <>
      <Box className={classes.userDetails} onKeyDown={onKeyDown}>
        <Box width={'100%'}>
          <JoinFormGreeting />
        </Box>
        <NameInput handleJoinMeeting={handleJoinMeeting} />
        <EmailInput handleJoinMeeting={handleJoinMeeting} />
        <Box mt={4} width={'100%'}>
          <Typography
            className={classes.avSettingsLink}
            component="span"
            onClick={handleSettingsClicked}
            data-testid="avSettings"
          >
            {`${intl.get('audioVideoSettings')} >`}
          </Typography>
          <Suspense fallback={<LoadingComp />}>
            <AudioVideoSettings open={avDrawerOpen} toggleDrawer={toggleAvDrawer} />
          </Suspense>
        </Box>
        <TermsOfService />
      </Box>
      <JoinButton
        isCalling={hasClickedJoinMeeting}
        hasError={false}
        handleJoinMeeting={handleJoinMeeting}
        isDisabled={!guestForm?.tosCheckbox || !guestForm?.name}
        extension={guestForm?.extension}
      />
    </>
  );
};

const userFieldStyles = makeStyles(() => ({
  userDetails: {
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: '100%',
    width: '500px'
  },
  formField: {
    maxWidth: '500px',
    width: '100%'
  },
  avSettingsLink: {
    color: colors.lifesize,
    cursor: 'pointer'
  },
  validating: {
    minHeight: '28px'
  }
}));
